import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  /** Stable id used as filter value. */
  value: string;
  /** Human label shown in the list and on the trigger summary. */
  label: string;
  /** Optional Tailwind classes for the leading dot — keeps the chip palette
   *  consistent with the cards. */
  dotClass?: string;
  /** Render the option text in mono (for stack names like `postgres`). */
  mono?: boolean;
}

interface Props {
  /** Trigger label, e.g. "Industry" or "Stack". */
  label: string;
  options: MultiSelectOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Placeholder for the search input inside the popover. */
  searchPlaceholder?: string;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  searchPlaceholder = "Search…",
}: Props) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.value.toLowerCase().includes(q) || o.label.toLowerCase().includes(q),
    );
  }, [options, query]);

  function toggle(value: string) {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  }

  function clearAll() {
    onChange(new Set());
  }

  const selectedCount = selected.size;
  const summary = (() => {
    if (selectedCount === 0) return "Any";
    if (selectedCount === 1) {
      const id = [...selected][0];
      return options.find((o) => o.value === id)?.label ?? id;
    }
    return `${selectedCount} selected`;
  })();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 h-8 px-3 rounded-md border text-xs font-medium transition-colors",
            selectedCount > 0
              ? "border-foreground/30 bg-muted/40 text-foreground"
              : "border-border text-foreground hover:bg-muted",
          )}
        >
          <span className="text-muted-foreground">{label}</span>
          <span className="font-semibold">{summary}</span>
          <ChevronDown size={12} className="text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full h-8 pl-7 pr-2 rounded-md bg-transparent text-xs text-foreground placeholder:text-muted-foreground border border-border focus:border-foreground/40 outline-none"
            />
          </div>
        </div>
        <ul className="max-h-64 overflow-auto py-1">
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-muted-foreground">No matches.</li>
          )}
          {filtered.map((o) => {
            const isOn = selected.has(o.value);
            return (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => toggle(o.value)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors",
                    isOn ? "text-foreground" : "text-foreground",
                    "hover:bg-muted",
                  )}
                >
                  <span
                    className={cn(
                      "h-3.5 w-3.5 rounded-sm border flex items-center justify-center flex-shrink-0",
                      isOn ? "bg-primary border-primary" : "border-border",
                    )}
                    aria-hidden
                  >
                    {isOn && <Check size={10} className="text-primary-foreground" />}
                  </span>
                  {o.dotClass && (
                    <span
                      aria-hidden
                      className={cn("h-1.5 w-1.5 rounded-full flex-shrink-0", o.dotClass)}
                    />
                  )}
                  <span className={cn("flex-1 truncate", o.mono && "font-mono")}>
                    {o.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {selectedCount > 0 && (
          <div className="p-1.5 border-t border-border flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground px-1.5">
              {selectedCount} selected
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="text-[11px] font-medium text-foreground hover:underline px-1.5"
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
